<template>
	<div v-if="showUI" class="container">

		<div v-if="gettingLocation" class="text-center">
			Getting location... <spinner :inline="true" :small="true"></spinner>
		</div>

		<div v-if="locationBlocked" class="alert alert-warning">
			<i class="fa fa-exclamation-triangle"></i>
			<p>Unable to get device Location -- device location permission was denied. You need to enable permissions for this site to use Location. When you have done that, refresh the page or click the button below.</p>

			<div class="text-center">
				<button class="btn btn-light" v-on:click="reload">
					<i class="fa fa-sync"></i>
					Try Again
				</button>
			</div>
		</div>
		<div v-else-if="unknownLocationError" class="alert alert-warning">
			<i class="fa fa-exclamation-triangle"></i>
			<p>Unable to get device Location -- unknown error. The attempt to get location failed due to a timeout. Make sure your location permissions are enabled, clear your site settings cache, then try again.</p>
		</div>

		<form v-if="clockUIState == 'out'" v-on:submit.prevent="clockIn">
	
			<h2 class="text-center">Clock In</h2>
			<div class="clockin-sections">
				<div v-if="scheduledEvent && (scheduledEvent.service_id > 0 || scheduledEvent.job_id > 0) && !log.job_id && !log.service_id" class="clockin-section text-center">
			
					<div>You are scheduled for
						<span v-if="scheduledEvent.Job" style="font-size: 16pt">Project #{{ scheduledEvent.Job.job_num }}</span>
						<span v-else-if="scheduledEvent.Service">Service #{{ scheduledEvent.Service.job_num }}</span>
					</div>
					<div v-if="scheduledEventIsNow" class="text-danger">HAPPENING NOW</div>
					<div v-else-if="scheduledEventTimeTo > 0">
						 Starting in {{ scheduledEventTimeTo }}
					</div>

					<button type="button" class="btn btn-success btn-lg mt-2" v-on:click="selectScheduledJob">
						<i class="fa fa-clock"></i>
						Select
					</button>

					<div class="mt-3 mb-3">
						Or pick a different job below
					</div>
				</div>

				<div class="clockin-section" :class="{'disabled' : log.job_id > 0 || log.service_id > 0}">
					<div v-if="!log.job_id && !log.service_id">
						<div class="suggested-jobs">
							<h3>1. Suggested Jobs</h3>

							<div v-if="suggestedJobsLoading" class="text-center">
								Loading suggestions... <spinner :inline="true" :small="true"></spinner>
							</div>

							<ul v-else-if="suggestedJobs.length > 0" class="list-group suggested-jobs-list">
								<list-job-tile
									v-for="job in suggestedJobs"
									:key="job.type + '-' + job.id"
									:action="true"
									:icon="suggestedJobIcon(job)"
									:title="job.job_name"
									:right-text="suggestedJobRightText(job)"
									:meta="suggestedJobMeta(job)"
									:customer-name="suggestedJobCustomer(job)"
									:address="suggestedJobAddress(job)"
									v-on:click.native="onSuggestedJobClick(job)">
								</list-job-tile>
							</ul>

							<p v-else class="text-muted">No suggested jobs right now</p>
						</div>

						<div class="job-type-fallback text-center">
							<div class="text-muted mb-2">Can't find your job?</div>
							<button class="btn btn-outline-secondary m-1" type="button" v-on:click="setClockInJobType('Project')">
								<i class="fa fa-cubes"></i>
								Project
							</button>
							<button class="btn btn-outline-secondary m-1" type="button" v-on:click="setClockInJobType('Service')">
								<i class="fa fa-wrench"></i>
								Service
							</button>
						</div>

						<job-picker
							v-if="clockInJobType == 'Project'"
							class="mt-3"
							:hide-label="true"
							job-type="project"
							:hide-archived="true"
							key="project"
							v-on:change="setProject($event,'project')"
							v-model="log.job_id">
						</job-picker>

						<job-picker
							v-else-if="clockInJobType == 'Service'"
							class="mt-3"
							:hide-label="true"
							job-type="service"
							:hide-archived="true"
							:hide-completed="true"
							key="service"
							v-on:change="setProject($event,'service')"
							v-model="log.service_id">
						</job-picker>
					</div>
					<div v-else class="clockin-section-value" v-on:click="clearSelectedJob">
						<job-picker
							v-if="clockInJobType == 'Project'"
							:hide-label="true"
							job-type="project"
							key="project-selected"
							v-on:change="setProject($event,'project')"
							:readonly="true"
							v-model="log.job_id">
						</job-picker>

						<job-picker
							v-else-if="clockInJobType == 'Service'"
							:hide-label="true"
							job-type="service"
							key="service-selected"
							v-on:change="setProject($event,'service')"
							:readonly="true"
							v-model="log.service_id">
						</job-picker>
					</div>
				</div>

				<div v-if="isOffsite && (log.job_id > 0 || log.service_id > 0)" class="clockin-section" :class="{'disabled' : activeClockinSection != 'offsite'}">
					<h3 v-on:click="activeClockinSection = 'offsite'">2. Offsite</h3>

					<div v-if="activeClockinSection == 'offsite'" class="form-control-plaintext">
						<span v-if="distanceToJob.metres.center <= project.check_in_tolerance">You are on site ({{distanceToJob.distance.center}}{{distanceToJob.unit}} from the project).</span>

						<span v-else-if="distanceToJob.metres.closest_margin == '[Unknown]' || distanceToJob.metres.closest_margin <= project.check_in_tolerance">
							The location data is not accurate enough to verify if you are on site <span v-if="currentPosition">({{distanceToJob.distance.center}}{{distanceToJob.unit}} from the project with error margin of {{currentPositionError}})</span>. <strong>We'll give you the benefit of the doubt and assume you're onsite!</strong>
						</span>

						<template v-else>
							You are <strong>
								{{convertToRegionalUnits(distanceToJob.distance.center,distanceToJob.unit,1).value}}
								{{convertToRegionalUnits(distanceToJob.distance.center,distanceToJob.unit,1).unit}}
							</strong> from the project.

							<span class="text-danger">Please provide a reason why you are not on site.</span>
						</template>
					</div>

					<form-group v-if="activeClockinSection == 'offsite' && !distanceToJob && (log.job_id || log.service_id)" label="Your Location" col-class="col-md-4">
						<div class="form-control-plaintext"><i class="fa fa-exclamation-triangle"></i> Unable to calculate distance to job.</div>
					</form-group>

					<template v-if="currentPosition && (log.job_id || log.service_id) && distanceToJob.metres.closest_margin > project.check_in_tolerance" >
						<field 
							label="Reason for not being on site" 
							col-class="col-md-4" 
							v-model="reasonNotOnSiteSelected" 
							v-on:change="setReason"
							:params="{
								type: 'select',
								options:[{value:'At the shop',text:'At the shop'},{value:'At the supplier',text:'At the supplier'},{value:'En route',text:'En route'},{value:'Working from Home',text:'Working from Home'},{value:'other',text:'Other'},]
							}">
						</field>

						<field v-if="reasonNotOnSiteSelected == 'other'" 
							v-on:change="setReason" 
							v-model="typedReason" 
							label=" "
							col-class="col-md-4" 
							:params="{type:'textarea',placeholder:'Please define...'}">
						</field>

						<field label="When do you expect to be on site?"
							col-class="col-md-4"
							v-model="onSiteEstimate"
							v-on:change="setOnsiteEstimate"
							:params="{
								type: 'select',
								options: [
									{value: 'never',text:'Not at all'},
									{value: 15,text:'15 min'},
									{value: 30,text:'30 min'},
									{value: 45,text:'45 min'},
									{value: 60,text:'1 hr'},
									{value: 120,text:'2 hr'},
									{value: 180,text:'3 hr'},
									{value: 240,text:'4 hr'},
									{value: 300,text:'5 hr'},
									{value: 360,text:'6 hr'},
									{value: 420,text:'7 hr'},
									{value: 480,text:'8 hr'}
								]
							}">
							<div class="text-muted">We will send you a reminder notification to clock in On-site</div>
						</field>
					</template>

					<div class="text-center" v-if="!mapToggle">
						<button class="btn btn-light" v-on:click="mapToggle = true">
							<i class="fa fa-map"></i>
							Show Map
						</button>
					</div>
					<job-map v-if="mapToggle && (log.job_id || log.service_id)"
						:current-position="currentPosition" 
						:job-location="projectLocation" 
						:show-map="showMap" 
						:map-zoom="mapZoom"
						:check-in-tolerance="parseInt(project.check_in_tolerance)">
					</job-map>
				</div>
				<div class="clockin-section" :class="{'disabled' : activeClockinSection != 'equipment'}">
					<h3 v-on:click="activeClockinSection = 'equipment'">3. Equipment</h3>
					<template v-if="activeClockinSection == 'equipment' || equipmentId">
						<form-group label="Are you in a piece of equipment?"
							col-class="col-md-6">

							<field :params="{type:'btn-group',buttons:[
									{text: 'Yes',value: true},
									{text: 'No', value: false}
								]}"
								:readonly="activeClockinSection != 'equipment'"
								v-model="isInEquipment"
								v-on:change="isInEquipmentChange">
							</field>

							<equipment-picker v-if="isInEquipment"
								v-model="equipmentId"
								:readonly="activeClockinSection != 'equipment'"
								v-on:equipmentChange="activeClockinSection = 'checkin'">
							</equipment-picker>
						</form-group>

						<template v-if="!equipmentId && suggestedEquipment && suggestedEquipment.length > 0">
							<h3>Suggested</h3>

							<div v-for="eq in suggestedEquipment" class="suggested-equipment d-flex flex-row flex-wrap justify-content-between align-items-center">
								<div>{{ eq.description }}</div>
								
								<div>
									<button class="btn btn-light" :class="{'active':eq.id == equipmentId}" v-on:click="selectSuggestedEquipment(eq)" type="button">
										<i class="fa" :class="{'fa-square':eq.id != equipmentId,'fa-check-square':eq.id == equipmentId}"></i>
										Select 
									</button>
								</div>
							</div>
						</template>
					</template>

				</div>
				<div class="clockin-section" :class="{'disabled' : activeClockinSection != 'checkin'}"> 

					<h3 v-on:click="activeClockinSection = 'checkin'">{{ isOffsite && (log.job_id > 0 || log.service_id > 0) ? 3 : 2 }}. Clock In</h3>


					<template>
						<template v-if="log.service_id || log.job_id">
							<a href="#" v-if="!showClockInRemarks" v-on:click.prevent="showClockInRemarks = true">
								Add Clock In Remarks
							</a>
							<field v-else label="Clock In Remarks" col-class="col-md-4" v-model="log.time_in_notes" :params="{type:'textarea',size:'xsm'}"></field>
						</template>

						<form-group v-if="showClockInButton" label="" col-class="col-md-4">
							<button type="submit" class="btn btn-success btn-lg" style="display: block;width: 100%">
								<i class="fa fa-clock"></i>
								Clock IN
							</button>
						</form-group>
					</template>
				</div>
			</div>
		</form>
		<form v-else-if="clockUIState == 'offsite'" v-on:submit.prevent="clockInOnSite">
			<h2>Clocked in OFFSITE</h2>

			<button v-if="!isOffsite" class="btn btn-lg btn-success" style="display: block;width: 100%;margin-bottom: 10px;">
				<i class="fa fa-map-marker"></i>
				Check In ON SITE
			</button>

			<p class="text-center" v-if="distanceToJob && log.job_id">

				<span v-if="!isOffsite">You are on site (
					{{convertToRegionalUnits(distanceToJob.distance.center,distanceToJob.unit,1).value}}
					{{convertToRegionalUnits(distanceToJob.distance.center,distanceToJob.unit,1).unit}}
					from the project).
				</span>

				<template v-else>
					You are
					<strong>
						{{convertToRegionalUnits(distanceToJob.distance.center,distanceToJob.unit,1).value}}
						{{convertToRegionalUnits(distanceToJob.distance.center,distanceToJob.unit,1).unit}}
					</strong> 
					from the project. You need to be physically on site in order to check out as on site.
				</template>

			</p>

			<p v-if="!distanceToJob && log.job_id" class="text-center">
				<i class="fa fa-exclamation-triangle"></i> Unable to calculate distance to job. Please ensure your browser <strong>Location</strong> permissions are enabled for this site
			</p>

			<job-map 
				:current-position="currentPosition" 
				:job-location="projectLocation" 
				:show-map="showMap" 
				:map-zoom="mapZoom"
				:check-in-tolerance="parseInt(project.check_in_tolerance)">
			</job-map>

			<button 
				type="button" 
				class="btn btn-success btn-lg" 
				style="display: block;width: 100%"
				v-on:click="clockOutOffsiteClick">
				<i class="fa fa-clock"></i>
				Clock OUT<br>(I worked completely offsite)
			</button>
		</form>
		<form v-else-if="clockUIState == 'in'" v-on:submit.prevent="clockOut">

			<!--<h2 class="offset-md-4">Clocked IN</h2>-->

			<form-group v-if="!changingJobClockout" :label="projectType" col-class="col-md-4 col-4 col-sm-4">
				<div class="form-control-plaintext">{{project.job_num}}: {{project.job_name}} <a href="#" v-on:click.prevent="changingJobClockout = true"><small>Change</small></a></div>
			</form-group>

			<div v-else>

				<h5>Update Service/Project</h5>

				<job-picker
					v-if="log.service_id == 0 || !log.service_id" 
					label="Project"
					col-class="col-6 col-sm-4"
					job-type="project"
					:hide-archived="true"
					key="project"
					v-on:change="updateProject($event,'project')" 
					v-model="log.job_id">
				</job-picker>

				<job-picker
					v-if="log.job_id == 0 || !log.job_id" 
					label="Service"
					col-class="col-6 col-sm-4"
					job-type="service"
					:hide-archived="true"
					:hide-completed="true"
					key="service"
					v-on:change="updateProject($event,'service')" 
					v-model="log.service_id">
				</job-picker>

			</div>

			<form-group label="Last Clocked IN" col-class="col-md-4 col-4 col-sm-4">
				<div class="form-control-plaintext"><strong v-if="log.elapsed">{{ log.elapsed }} hrs ago</strong> ({{ formatDateTimeToLocal(log.time_in) }})</div>
			</form-group>

			<form-group v-for="(payBoost,index) in project.PayBoost" :label="payBoost.description" :key="'payboosts-'+payBoost.id" col-class="col-md-4 col-8 col-sm-4">

				<field v-if="log.payBoosts && payBoost.id in log.payBoosts" :params="{type: 'boolean'}" v-model="log.payBoosts[payBoost.id]"></field>
			</form-group>

			<form-group label="Regular Hours" col-class="col-sm-4">

				<div class="row">
					<div class="col-7">
						<field v-model="log.regular_hours" :params="{'type': 'number',min:0,max:24,step:0.25,placeholder:'0.00'}"></field>
					</div>
					<div class="col-5">
						<button type="button" class="btn btn-light" v-if="!overTimeHours" v-on:click="overTimeHours = true" style="width: 100%;">
							<i class="fa fa-plus"></i>
							Overtime
						</button>
					</div>
				</div>

			</form-group>

			<div v-if="overTimeHours">

				<h4 class="mt-4">Overtime Hours</h4>

				<field v-if="$store.timeLogSettings.uses_overtime_15x == 1" 
					label="Overtime x1.5" 
					col-class="col-6 col-sm-4" 
					v-model="log.overtime_hours_15x" 
					:params="{'type': 'number',min:0,max:24,step:0.25,placeholder:'0.00'}">	
				</field>

				<field v-if="$store.timeLogSettings.uses_overtime_2x == 1" 
					label="Overtime x2.0" 
					col-class="col-6 col-sm-4" 
					v-model="log.overtime_hours_2x" 
					:params="{'type': 'number',min:0,max:24,step:0.25,placeholder:'0.00'}">
				</field>

				<field v-if="$store.timeLogSettings.uses_overtime_25x == 1" 
					label="Overtime x2.5" 
					col-class="col-6 col-sm-4" 
					v-model="log.overtime_hours_25x" 
					:params="{'type': 'number',min:0,max:24,step:0.25,placeholder:'0.00'}">	
				</field>

			</div>

			<field label="Clock Out Remarks" 
				col-class="col-md-4" 
				v-model="log.time_out_notes" 
				:params="{type:'textarea',size:'xsm'}">
			</field>

			<div v-if="projectType == 'Project' && project.detailed_timelogs == 1">
				<hr>
				<h4>Detailed Reporting</h4>

				<p v-if="!projectHasBid">This project has no bid. A detailed report is only needed if you are claiming time to a change order.</p>
				<p v-else>This project has detailed time log reporting turned on, so please tell us how you spent your time. This will help in project analytics as well as help us compile a comprehensive work history for you.</p>

				<detailed-time-allocator v-model="reportedComponentWork" :project="project" :log="log"></detailed-time-allocator>
			</div>

			<button type="submit" class="btn btn-success btn-lg mb-4" :disabled="!showClockOutButton" style="display: block;width: 100%">
				<i class="fa fa-clock"></i>
				Clock OUT
			</button>	

			<div class="text-center" v-if="!mapToggle">
				<button v-on:click="toggleMap" class="btn btn-light" type="button">
					<i class="fa fa-map"></i>
					Show Map
				</button>
			</div>

			<job-map v-if="mapToggle"
				:current-position="currentPosition" 
				:job-location="projectLocation" 
				:show-map="showMap" 
				:map-zoom="mapZoom"
				:check-in-tolerance="parseInt(project.check_in_tolerance)">
			</job-map>
	
		</form>

		<referral-link-banner style="margin-top: 20px;"
			:has-button="true"
			v-on:click="$bus.$emit('openReferralModal','Referral Link: Punch Clock Page')">
		</referral-link-banner>

	</div>
</template>

<script>
import FormGroup from "../components/FormGroup"
import Field from "../components/Field"
import JobMap from "../components/JobMap"
import JobPicker from "@/components/super_components/JobPicker"
import DetailedTimeAllocator from "@/components/super_components/DetailedTimeAllocator"
import EquipmentPicker from "@/components/equipment_picker/EquipmentPicker"
import ReferralLinkBanner from "@/components/banner_ads/ReferralLinkBanner"

import Spinner from "@/components/Spinner"
import ListJobTile from "@/components/ListJobTile"

import JobMixins from "@/mixins/JobMixins.js"
import AppStartup from "@/mixins/AppStartup.js"

export default {

	name: 'PunchClock',
	components: {
		FormGroup,
		Field,
		JobMap,
		ReferralLinkBanner,
		DetailedTimeAllocator,
		JobPicker,
		EquipmentPicker,
		Spinner,
		ListJobTile
	},
	mixins: [JobMixins,AppStartup],
	data() {
		return {
			clockingIn: false,
			clockInJobType: null,
			activeClockinSection: 'where',
			clockUIState: "", //out, offsite, in
			showUI: false,
			overTimeHours: false,
			showClockInRemarks: false,
			lat: 0,
			long: 0,
			projectMapIcon: window.apiRoot+"resources/icons/hardhat_36_point.png",
			userMapIcon: window.apiRoot+"resources/icons/location_36.png",
			log: {
				job_id: null,
				service_id: null,
				lat: null,
				long: null,
				position_accuracy: null,
				reason_not_on_site: null,
				regular_hours: null,
				overtime_hours_15x: null,
				overtime_hours_2x: null,
				overtime_hours_25x: null,
				payBoosts: {}, //this will get discarded by API
				PayBoost: [] //primitized -- this is what the API will care about
			},
			overtimeMultiplierOptions: [
				{
					value: 1.5,
					text: "x1.5"
				},
				{
					value: 2.0,
					text: "x2"
				},
				{
					value: 2.5,
					text: "x2.5"
				}
			],
			project: {}, //can be project OR service
			projectLocation: {},
			projectType: 'Project', //Project or Service
			userTimeLogs: [],
			userScheduleEvents: [],
			reasonNotOnSiteSelected:"",
			typedReason: "",
			onSiteEstimate: null,
			currentPosition: {
				lat: null,
				long: null,
				accuracy: null
			},
			isOnSite: false,
			isInEquipment: null,
			equipmentId: null,
			locationBlocked:false,
			positionRefreshTimer: null,
			clockInOtherUser: false,
			otherUserId: null,
			unknownLocationError: false,
			suggestedJobs: [],
			suggestedEquipment: [],
			suggestedJobsLoading: false,
			mapToggle: false,
			reportedComponentWork: [],
			showComponentPicker: false,
			changeOrders: [],
			changingJobClockout: false,
			gettingLocation: false
		}
	},
	watch: {
		'$store.latestPosition'() {
			this.currentPosition = this.$store.latestPosition
		}
	},
	computed: {
		scheduledEventIsNow() {
			if (this.scheduledEvent && this.moment.utc(this.scheduledEvent.start_time).isBefore(this.moment()))
				return true

			else
				return false
		},
		scheduledEvent() {
			if (this.userScheduleEvents.length > 0)
				return this.userScheduleEvents[0]

			return false
		},
		scheduledEventTimeTo() {
			if (this.scheduledEvent) {
				let minutes = this.moment.utc(this.scheduledEvent.start_time).diff(this.moment(),"minutes")

				if (minutes > 60) {
					let hours = minutes / 60
					hours = hours.round(1)

					return hours + " hours"
				}
				return minutes + " minutes"
			}
			return 0
		},
		showMap() {

			if (!isNaN(this.currentPosition.lat) && 
				!isNaN(this.currentPosition.lng) && 
				!isNaN(this.projectLocation.lat) && 
				!isNaN(this.projectLocation.lng)) {

				return true
			}
			else
				return false
		},
		showClockInButton() {

			if (!this.log.job_id && !this.log.service_id)
				return false

			if (this.distanceToJob.metres.closest_margin == '[Unknown]' || this.distanceToJob.metres.closest_margin <= this.project.check_in_tolerance)
				return true

			if ((this.distanceToJob.metres.closest_margin > +this.project.check_in_tolerance || this.distanceToJob.metres.closest_margin == '[Unknown]') && this.log.reason_not_on_site) 
				return true
			
			else if (!this.distanceToJob && this.log.job_id) 
				return true
			else if (this.distanceToJob.metres.closest_margin <= +this.project.check_in_tolerance)
				return true
			else if (!this.currentPosition)
				return true
			else
				return false
		},
		showClockOutButton() {
			if (this.log.regular_hours == 0 && this.log.overtime_hours_15x == 0 && this.log.overtime_hours_2x == 0 && this.log.overtime_hours_25x == 0) 
				return false

			if (this.needsDetailedReport && !this.detailedHoursMatchClaim)
				return false

			return true
		},
		projectHasBid() {
			if (!this.project || !this.project.JobBid)
				return false

			let activeBid = this.project.JobBid.find(bid => bid.is_active == 1)
			if (activeBid)
				return true

			return false
		},
		needsDetailedReport() {
			if (this.projectType != 'Project' || !this.project || this.project.detailed_timelogs != 1)
				return false

			if (this.projectHasBid)
				return true

			let changeOrderClaim = this.reportedComponentWork.find(item => item.change_quote_id)
			if (changeOrderClaim)
				return true

			return false
		},
		totalDetailedHours() {
			let hrTypes = ["regular_hours","overtime_hours_15x","overtime_hours_2x","overtime_hours_25x"]

			let totals = {
				total: 0
			}

			hrTypes.forEach(type => {
				let total = this.reportedComponentWork.reduce((acc, item) => type in item && item[type] ? acc + +item[type] : acc,0)

				if (isNaN(total))
					total = 0

				totals[type] = total
				totals.total += total
			})

			return totals
		},
		detailedHoursMatchClaim() {
			let vm = this
			let hrTypes = ["regular_hours","overtime_hours_15x","overtime_hours_2x","overtime_hours_25x"]
			let totalClaim = 0

			let allMatch = true
			hrTypes.forEach(type => {
				totalClaim += +vm.log[type]
				if (type in vm.log && type in vm.totalDetailedHours && vm.totalDetailedHours[type] != vm.log[type]) 
					allMatch = false
			})

			if (totalClaim == 0)
				return false

			return allMatch
		},
		mapZoom() {
			//set map zoom depending on distance between user and project

			if (this.distanceToJob.metres.center < 1000)
				return 14;
			else if (this.distanceToJob.metres.center < 5000)
				return 11;
			else if (this.distanceToJob.metres.center < 25000)
				return 9;
			else if (this.distanceToJob.metres.center < 250000)
				return 6;
			else
				return 5;
		},
		currentPositionError() {
			if (this.currentPosition.accuracy > 5000)
				return Math.round(this.currentPosition.accuracy/100)/10 + " km"
			else {
				let rounded = +this.currentPosition.accuracy
				rounded = rounded.round(0)
				return rounded + " metres"
			}
		},
		isOffsite() {
			console.log(this.distanceToJob, this.project.check_in_tolerance)
			if (this.distanceToJob.metres.closest_margin == '[Unknown]')
				return true

			return this.distanceToJob.metres.closest_margin > +this.project.check_in_tolerance
		},
		distanceToJob() {
			if (!this.currentPosition || !this.currentPosition.lat || !this.currentPosition.lng || !this.project || !this.project.lat || !this.project.long) {

				return {
					distance: {
						center: "[Unknown]",
						closest_margin: "[Unknown]",
						furthest_margin: "[Unknown]"
					},
					unit: "km",
					metres: {
						center: "[Unknown]",
						closest_margin: "[Unknown]",
						furthest_margin: "[Unknown]"
					}
				}
			}

			let distance = this.getDistance(this.currentPosition.lat,this.currentPosition.lng,this.project.lat, this.project.long)


			if (!distance && distance !== 0)
				return false

			// Calculate in either km or metres depending on distance from project
			// return distance in 3 forms: centered, closest possible & furthest possible base on accurance margin
			if (distance > 2500) {
				let distance_center = Math.round(distance/1000)

				let closest_margin_m = distance-this.currentPosition.accuracy

				if (closest_margin_m < 0)
					closest_margin_m = 0

				return {
					distance: {
						center: distance_center,
						closest_margin: closest_margin_m/1000,
						furthest_margin: (distance+this.currentPosition.accuracy)/1000
					},
					unit: "km",
					metres: {
						center: Math.round(distance),
						closest_margin: closest_margin_m,
						furthest_margin: distance+this.currentPosition.accuracy
					}
				}
			}
			else {
				let distance_center = Math.round(distance)

				let closest_margin = distance_center-this.currentPosition.accuracy

				if (closest_margin < 0)
					closest_margin = 0

				return {
					distance: {
						center: Math.round(distance),
						closest_margin: closest_margin,
						furthest_margin: distance_center+this.currentPosition.accuracy
					},
					unit: "m",
					metres: {
						center: Math.round(distance),
						closest_margin: closest_margin,
						furthest_margin: distance+this.currentPosition.accuracy
					}
				}
			}
		}
	},
	methods: {
		reload() {
			location.reload()
		},
		setClockInJobType(type) {
			this.clockInJobType = type
			this.activeClockinSection = 'where'
			this.log.job_id = 0
			this.log.service_id = 0

			if (type == "Project")
				this.fetchJobsOrServices("projects")
			else
				this.fetchJobsOrServices('services')
		},
		clearSelectedJob() {
			this.clockInJobType = null
			this.log.job_id = null
			this.log.service_id = null
			this.project = {}
			this.activeClockinSection = 'where'
		},
		toggleMap() {
			let vm = this
			this.mapToggle = true
			this.gettingLocation = true
			this.getLocation().then(pos => {
				vm.currentPosition = pos
				vm.gettingLocation = false
			})
		},
		setReason() {
			let vm = this

			this.$nextTick(() => {
				if (vm.reasonNotOnSiteSelected == "other") {
					vm.log.reason_not_on_site = vm.typedReason
				}
				else
					vm.log.reason_not_on_site = vm.reasonNotOnSiteSelected

				if (vm.log.reason_not_on_site && vm.onSiteEstimate)
					vm.activeClockinSection = "equipment"
			})
		},
		setOnsiteEstimate() {
			let vm = this

			this.$nextTick(() => {
				if (vm.log.reason_not_on_site && vm.onSiteEstimate)
					vm.activeClockinSection = "equipment"
			})
		},
		clockOutOffsiteClick() {
			this.clockUIState = 'in'

		},
		isInEquipmentChange(value) {
			if (!value) 
				this.activeClockinSection = "checkin"
			else 
				this.fetchSuggestedEquipment()
			
		},
		chooseAreasOfWork() {
			this.showComponentPicker = true

			if (this.changeOrders.length == 0) {
				this.fetchChangeOrders(this.project.id)
			}
		},
		fetchAndDoClockInPromptFormTemplates(forServices) {
			let vm = this

			let filters = []

			if (forServices) {
				filters.push(["clockin_prompt_services",1])
			}
			else
				filters.push(["clockin_prompt_projects",1])

			let params = {
				filters
			}

			return new Promise((resolve,reject) => {

				vm.getJSON("FormChildTemplate/get",params).then(response => {
					if (response.success && response.data.length > 0) {
						
						let emitPayload = {
							doChildTemplateIdList: response.data.map(template => template.id),
							allowForcedSubmit: true
						}
						if (vm.projectType == "Project")
							emitPayload.job_id = vm.project.id
						else
							emitPayload.service_id = vm.project.id

						vm.$bus.$emit("doCompanyForm",emitPayload)
						resolve(response)
					}
					else
						resolve(false)
				})

			})
		},
		selectScheduledJob() {

			let type, projectType

			if (this.scheduledEvent.job_id > 0) {

				type = "projects"
				this.setClockInJobType('Project')		
				this.setProject(this.scheduledEvent.job_id,"project")

				this.log.job_id = this.scheduledEvent.job_id
				this.log.service_id = 0
			}
			else if (this.scheduledEvent.service_id > 0) {

				type = "services"
				this.setClockInJobType('Service')
				this.setProject(this.scheduledEvent.service_id,"service")

				this.log.service_id = this.scheduledEvent.service_id
				this.log.job_id = 0	
			}	
		},
		clockIn() {
			let vm = this

			vm.log.lat = vm.currentPosition.lat
			vm.log.long = vm.currentPosition.lng
			vm.log.position_accuracy = vm.currentPosition.accuracy

			let payload = {
				job_id: vm.log.job_id,
				service_id: vm.log.service_id,
				reason_not_on_site: vm.log.reason_not_on_site,
				time_in_notes: vm.log.time_in_notes
			}

			if (this.currentPosition) {
				payload.currentLong = this.currentPosition.lng
				payload.currentLat = this.currentPosition.lat
				payload.position_accuracy = this.currentPosition.accuracy
			}
			else {
				payload.currentLong = 0
				payload.currentLat = 0
				payload.position_accuracy = 0
				payload.no_location_clockin = 1
			}

			if (vm.onSiteEstimate)
				payload.onSiteEstimateMin = vm.onSiteEstimate

			if (this.clockInOtherUser)
				payload.user_id = this.otherUserId

			if (this.isInEquipment && this.equipmentId)
				payload.equipment_id = this.equipmentId

			this.postData("TimeLog/punch-clock",payload,true).then(function(response){
				if (response.success) {
					vm.clockIn = false
					vm.log.reason_not_on_site = ""
					vm.fetchData()

					if (!vm.clockInOtherUser) {
						let clockInState = "onsite"
						if ("clocked_in_offsite" in response.data && response.data.clocked_in_offsite == 1)
							clockInState = "offsite"

						vm.$bus.$emit("clockStatusChanged",clockInState)
					}

					if ("time_in" in response.data) {
						vm.$store.clockinTime = response.data.time_in
					}
					else
						vm.$store.clockinTime = null

					vm.fetchClockStatus()

					//redirect to project/service user clocked into
					if (payload.job_id > 0) {

						vm.projectType = "Project"

						vm.fetchAndDoClockInPromptFormTemplates(false).then(response => {
							//if (!response || !response.success) 
								vm.$router.push("/project/"+vm.project.slug+"/dashboard")
						})
							
					}
					else {
						vm.projectType = "Service"

						vm.fetchAndDoClockInPromptFormTemplates(true).then(response => {
							//if (!response || !response.success) 
								vm.$router.push("/service/"+vm.project.slug+"/worksheet")
						})
					}

					
				}
			})
		},
		detailedClaimHasHours(item) {
			let hrTypes = ["regular_hours","overtime_hours_15x","overtime_hours_2x","overtime_hours_25x"]
			let total = hrTypes.reduce((acc, type) => type in item && item[type] ? acc + +item[type] : acc, 0)

			return total > 0
		},
		clockOut() {
			let vm = this

			let payload = {
				id: vm.log.id,
				request: "clockOut",
				job_id: vm.log.job_id,
				service_id: vm.log.service_id,
				regular_hours: vm.log.regular_hours,
				overtime_hours_15x: vm.log.overtime_hours_15x,
				overtime_hours_2x: vm.log.overtime_hours_2x,
				overtime_hours_25x: vm.log.overtime_hours_25x,
				time_out_notes: vm.log.time_out_notes
			}

			if (this.currentPosition) {
				payload.currentLat = this.currentPosition.lat
				payload.currentLong = this.currentPosition.lng
				payload.position_accuracy = this.currentPosition.accuracy
			}
			else {
				payload.currentLat = 0
				payload.currentLong = 0
				payload.position_accuracy = 0
				payload.no_location_clockout = 1
			}

			if (this.clockInOtherUser)
				payload.user_id = this.otherUserId

			if (vm.log.payBoosts) {
				payload.PayBoost = []
				Object.keys(vm.log.payBoosts).forEach(pbId => {
					if (vm.log.payBoosts[pbId])
						payload.PayBoost.push(pbId)
				})
			}

			this.postData("TimeLog/punch-clock",payload).then(function(response){
				if (response.success) {

					let detailedClaimRecords = vm.reportedComponentWork
						.filter(item => vm.detailedClaimHasHours(item))
						.map(item => {

							let payloadObj = {
								timelog_id: response.data.record_id,
								regular_hours: item.regular_hours,
								overtime_hours_15x: item.overtime_hours_15x,
								overtime_hours_2x: item.overtime_hours_2x,
								overtime_hours_25x: item.overtime_hours_25x
							}

							if ("component_id" in item)
								payloadObj.job_bid_component_id = item.component_id

							if ("change_quote_id" in item)
								payloadObj.change_quote_id = item.change_quote_id

							return payloadObj
						})

					if (detailedClaimRecords.length > 0) {
						vm.postData("TimeLogDetailedClaim/save-batch", {
							records: JSON.stringify(detailedClaimRecords)
						})
					}

					if (!vm.clockInOtherUser) 
						vm.$bus.$emit("clockStatusChanged",false)

					if (vm.log.service_id > 0)
						//redirect to services list page
						vm.$router.push("/services")

					vm.clockUIState = "out"
					vm.clockingIn = false,
					vm.clockInJobType = null
					vm.activeClockinSection = 'where'
					vm.log.job_id = null
					vm.log.service_id = null
					vm.log.lat_clockout_ = null
					vm.log.long_clockout = null
					vm.log.position_accuracy_clockout = null

					if ("time_in" in response.data)
						vm.$store.clockinTime = response.data.time_in
					else
						vm.$store.clockinTime = null

					vm.project = {}

					vm.fetchClockStatus()

					vm.fetchData()
					vm.loadSuggestedJobs()
				}
				else {
					vm.alertErrors(response.errors,false)
				}
			})
		},
		clockInOnSite() {
			let vm = this

			if (this.currentPosition) {
				this.log.currentLat = vm.currentPosition.lat
				this.log.currentLong = vm.currentPosition.lng 
				this.log.currentPositionError = vm.currentPosition.accuracy
				this.log.no_location_clockin = 0
			}
			else
				this.log.no_location_clockin = 1

			this.postData("TimeLog/punch-clock",this.log,true).then(response => {

				if (response.success)
					vm.$store.clockedIn = 'onsite'

				if ("time_in" in response.data)
					vm.$store.clockinTime = response.data.time_in
				else
					vm.$store.clockinTime = null

				vm.$router.push("/project/"+vm.project.slug +"/dashboard")

			})
		},
		setProject(id,type) {

			if (id > 0) {
				let vm = this

				let promise

				if (type == "project")
					promise = vm.fetchProject(false,id)
				else if (type == 'service') 
					promise = this.getJSON("Service/find/" + id)

				promise.then(response => {

					if (response.success)
						vm.project = response.data

					if (vm.project) {
						vm.projectLocation = {
							lat: +vm.project.lat,
							lng: +vm.project.long
						}
					}

					vm.gettingLocation = true

					this.getLocation().then(locationResponse => {

						vm.currentPosition = locationResponse

						vm.gettingLocation = false

						if (vm.isOffsite)
							vm.activeClockinSection = "offsite"
						else
							vm.activeClockinSection = "equipment"

						vm.log.time_in_notes = ""

					}).catch(error => {
						vm.unknownLocationError = true

						if (vm.isOffsite)
							vm.activeClockinSection = "offsite"
						else
							vm.activeClockinSection = "equipment"

						vm.gettingLocation = false
					})

					this.log.time_in_notes = ""

				})
			}
		},
		updateProject(id,type) {
			if (id > 0) {
				this.changingJobClockout = false
				this.setProject(id,type)
			}
		},
		suggestedJobClockin(job) {
			if (job.type == 'project') {
				this.clockInJobType = 'Project'
				this.log.job_id = job.id
				this.log.service_id = 0
			}
			else {
				this.clockInJobType = 'Service'
				this.log.job_id = 0
				this.log.service_id = job.id
			}

			let vm = this

			vm.log.lat = +job.lat
			vm.log.long = +job.long
			vm.log.position_accuracy = 1

			vm.setProject(job.id,job.type)

			vm.clockIn()
		},
		selectSuggestedJob(job) {
			if (job.type == 'project') {
				this.clockInJobType = 'Project'
				this.log.job_id = job.id
				this.log.service_id = 0
			}
			else {
				this.clockInJobType = 'Service'
				this.log.job_id = 0
				this.log.service_id = job.id
			}

			let vm = this
			this.$nextTick(() => {
				vm.log.lat = +job.lat
				vm.log.long = +job.long
				vm.log.position_accuracy = 1

				vm.setProject(job.id,job.type)
			})

		},
		selectSuggestedEquipment(eq) {
			this.equipmentId = eq.id
			this.activeClockinSection = 'checkin'
		},
		onSuggestedJobClick(job) {
			this.selectSuggestedJob(job)
		},
		suggestedJobIcon(job) {
			if (job.type == "service")
				return "fa-wrench"
			return "fa-cube"
		},
		suggestedJobMeta(job) {
			if (job.type == "service")
				return "Service #" + job.job_num
			return "Project #" + job.job_num
		},
		suggestedJobCustomer(job) {
			if (job.Customer)
				return job.Customer.name
			return ""
		},
		suggestedJobAddress(job) {
			if (!job.address)
				return ""
			if (job.city)
				return job.address + ", " + job.city
			return job.address
		},
		suggestedJobRightText(job) {
			let reasons = []

			if (job.ScheduleEvent)
				reasons.push("Scheduled On")

			if (job.TimeLog)
				reasons.push("Recent Clock-in")

			if (job.isClosest && !isNaN(job.distanceKm)) {
				let converted = this.convertToRegionalUnits(job.distanceKm, "km", 1)
				reasons.push(converted.value + converted.unit + " away")
			}

			let roleTitle = ""
			if (job.JobUser && job.JobUser.JobPersonnelRole && job.JobUser.JobPersonnelRole.title)
				roleTitle = job.JobUser.JobPersonnelRole.title
			else if (job.ServiceUser && job.ServiceUser.JobPersonnelRole && job.ServiceUser.JobPersonnelRole.title)
				roleTitle = job.ServiceUser.JobPersonnelRole.title

			if (roleTitle)
				reasons.push("Assigned as " + roleTitle.trim())

			return reasons.join(", ")
		},
		isSuggestedJobHappeningNow(job) {
			if (!job.ScheduleEvent || !job.ScheduleEvent.start_time)
				return false

			let now = this.moment()
			let start = this.moment.utc(job.ScheduleEvent.start_time)

			if (start.isAfter(now))
				return false

			if (job.ScheduleEvent.end_time)
				return this.moment.utc(job.ScheduleEvent.end_time).isAfter(now)

			return !start.isBefore(this.moment().subtract(1, "hour"))
		},
		addDistanceToJobs(jobs) {
			let vm = this

			return jobs.map(job => {
				if (!vm.currentPosition || !vm.currentPosition.lat)
					return job

				let distanceM = vm.getDistance(
					vm.currentPosition.lat,
					vm.currentPosition.lng,
					job.lat,
					job.long
				)

				let isOnSite = false
				if (distanceM < job.check_in_tolerance)
					isOnSite = true

				return Object.assign({}, job, {
					distanceM: distanceM,
					distanceKm: distanceM / 1000,
					isOnSite: isOnSite
				})
			})
		},
		sortAndTrimSuggestedJobs(list,maxCount) {
			let sorted = list.sort((jobA, jobB) => {
				// Scheduled always wins
				let scheduledA = !!jobA.ScheduleEvent
				let scheduledB = !!jobB.ScheduleEvent
				if (scheduledA != scheduledB) {
					if (scheduledA)
						return -1
					return 1
				}

				let onSiteA = !!jobA.isOnSite
				let onSiteB = !!jobB.isOnSite
				let recentA = !!jobA.TimeLog
				let recentB = !!jobB.TimeLog
				let roleA = !!(jobA.JobUser || jobA.ServiceUser)
				let roleB = !!(jobB.JobUser || jobB.ServiceUser)
				let nearbyA = !!jobA.isClosest
				let nearbyB = !!jobB.isClosest

				// Most matching reasons win
				let countA = 0
				if (onSiteA)
					countA++
				if (recentA)
					countA++
				if (roleA)
					countA++
				if (nearbyA)
					countA++

				let countB = 0
				if (onSiteB)
					countB++
				if (recentB)
					countB++
				if (roleB)
					countB++
				if (nearbyB)
					countB++

				if (countA != countB)
					return countB - countA

				// Same count: prefer on-site, then recent, then role, then nearby
				if (onSiteA != onSiteB) {
					if (onSiteA)
						return -1
					return 1
				}
				if (recentA != recentB) {
					if (recentA)
						return -1
					return 1
				}
				if (roleA != roleB) {
					if (roleA)
						return -1
					return 1
				}
				if (nearbyA != nearbyB) {
					if (nearbyA)
						return -1
					return 1
				}

				let distanceA = jobA.distanceM
				let distanceB = jobB.distanceM
				if (isNaN(distanceA))
					distanceA = Infinity
				if (isNaN(distanceB))
					distanceB = Infinity

				return distanceA - distanceB
			})

			return sorted.slice(0, maxCount)
		},
		loadSuggestedJobs() {
			let vm = this
			this.suggestedJobsLoading = true

			let lookbackStart = this.moment().subtract(1, "hour").format("YYYY-MM-DD HH:mm:ss")
			let dayEnd = this.moment().endOf("day").format("YYYY-MM-DD HH:mm:ss")
			let recentClockInStart = this.moment().subtract(7, "d").format("YYYY-MM-DD")

			let jobFields = [
				"id", "slug", "job_num", "job_name", "address", "city",
				"lat", "long", "check_in_tolerance","archived","status","date_completed","completed"
			]
			let contactInclude = [{model: "Contact", fields: ["name"]}]

			// 1. Scheduled for the current time (today, from 1 hour ago)
			let scheduleParams = {
				filters: [
					["user_id", this.$store.userDetails.id],
					["start_time >=", lookbackStart],
					["start_time <=", dayEnd],
					["is_published", 1]
				],
				orders: [["start_time", "ASC"]],
				include: [
					{model: "Job", fields: jobFields, include: contactInclude},
					{model: "Service", fields: jobFields, include: contactInclude}
				]
			}

			// 2. Projects/services the user has a role on
			let roleInclude = [
				{model: "JobPersonnelRole", fields: ["id", "title"]},
				{model: "Job", fields: jobFields, include: contactInclude}
			]
			let roleServiceInclude = [
				{model: "JobPersonnelRole", fields: ["id", "title"]},
				{model: "Service", fields: jobFields, include: contactInclude}
			]

			let roleJobUserParams = {
				filters: [
					["user_id", this.$store.userDetails.id]
				],
				fields: ["id", "job_id", "user_id", "personnel_role_id"],
				include: roleInclude
			}

			let roleServiceUserParams = {
				filters: [
					["user_id", this.$store.userDetails.id]
				],
				fields: ["id", "service_id", "user_id", "personnel_role_id"],
				include: roleServiceInclude
			}

			// 3. Clocked into in the last 7 days
			let recentClockInParams = {
				filters: [
					["user_id", this.$store.userDetails.id],
					["time_in > ", recentClockInStart]
				],
				fields: ["id", "slug", "job_id", "service_id"],
				include: [
					{
						model: "Job",
						fields: jobFields,
						include: contactInclude
					},
					{
						model: "Service",
						fields: jobFields,
						include: contactInclude
					}
				]
			}

			// 4. Active projects (not completed, not a bid, not archived)
			let activeProjectParams = {
				filters: [
					["archived", 0],
					["is_lead", 0],
					["date_completed", null]
				],
				fields: jobFields,
				include: contactInclude
			}

			let locationPromise = this.getLocation().then(position => {
				vm.currentPosition = position
				return position
			}).catch(() => {
				return null
			})

			Promise.all([
				locationPromise,
				this.getJSON("ScheduleEvent/get", scheduleParams),
				this.getJSON("JobUser/get", roleJobUserParams),
				this.getJSON("ServiceUser/get", roleServiceUserParams),
				this.getJSON("TimeLog/get", recentClockInParams),
				this.getJSON("Job/get", activeProjectParams)
			]).then(results => {
				let scheduleResponse = results[1]
				let roleJobUserResponse = results[2]
				let roleServiceUserResponse = results[3]
				let recentClockInResponse = results[4]
				let activeProjectsResponse = results[5]

				let suggestedList = []

				if (scheduleResponse.success) {

					scheduleResponse.data.forEach(scheduleEvent => {
						let job = null
						let type = null

						if (scheduleEvent.service_id > 0 && scheduleEvent.Service) {
							job = scheduleEvent.Service
							type = "service"
						}
						else if (scheduleEvent.job_id > 0 && scheduleEvent.Job) {
							job = scheduleEvent.Job
							type = "project"
						}

						if (job) {
							let record = vm.cloneObj(job)

							let event = vm.cloneObj(scheduleEvent)
							delete event.Job 
							delete event.Service

							record.type = type
							record.ScheduleEvent = event

							let matchIndex = suggestedList.findIndex(theJob => theJob.id == record.id && theJob.type == record.id)

							if (matchIndex >= 0) {
								//merge the record into match
								suggestedList[matchIndex].ScheduleEvent = event
							}
							else
								suggestedList.push(record)
						}
					})
				}

				if (roleJobUserResponse.success) {
					roleJobUserResponse.data.forEach(jobUser => {
						let job = null
						let type = null

						if (jobUser.Job) {
							job = jobUser.Job
							type = "project"
						}

						if (job) {
							let record = vm.cloneObj(job)

							let nested = vm.cloneObj(jobUser)
							delete nested.Job

							record.type = type
							record.JobUser = nested

							let matchIndex = suggestedList.findIndex(theJob => theJob.id == record.id && theJob.type == record.type)

							if (matchIndex >= 0)
								suggestedList[matchIndex].JobUser = nested
							else
								suggestedList.push(record)
						}
					})
				}

				if (roleServiceUserResponse.success) {
					roleServiceUserResponse.data.forEach(serviceUser => {
						let job = null
						let type = null

						if (serviceUser.Service) {
							job = serviceUser.Service
							type = "service"
						}

						if (job) {
							let record = vm.cloneObj(job)

							let nested = vm.cloneObj(serviceUser)
							delete nested.Service

							record.type = type
							record.ServiceUser = nested

							let matchIndex = suggestedList.findIndex(theJob => theJob.id == record.id && theJob.type == record.type)

							if (matchIndex >= 0)
								suggestedList[matchIndex].ServiceUser = nested
							else
								suggestedList.push(record)
						}
					})
				}

				if (recentClockInResponse.success) {
					recentClockInResponse.data.forEach(timeLog => {
						let job = null
						let type = null

						if (timeLog.service_id > 0 && timeLog.Service) {
							job = timeLog.Service
							type = "service"
						}
						else if (timeLog.job_id > 0 && timeLog.Job) {
							job = timeLog.Job
							type = "project"
						}

						if (job) {
							let record = vm.cloneObj(job)

							let nested = vm.cloneObj(timeLog)
							delete nested.Job
							delete nested.Service

							record.type = type
							record.TimeLog = nested

							let matchIndex = suggestedList.findIndex(theJob => theJob.id == record.id && theJob.type == record.type)

							if (matchIndex >= 0)
								suggestedList[matchIndex].TimeLog = nested
							else
								suggestedList.push(record)
						}
					})
				}

				if (activeProjectsResponse.success) {
					activeProjectsResponse.data.forEach(project => {
						let record = vm.cloneObj(project)

						record.type = "project"
						record.isClosest = true

						let matchIndex = suggestedList.findIndex(theJob => theJob.id == record.id && theJob.type == record.type)

						if (matchIndex >= 0)
							suggestedList[matchIndex].isClosest = true
						else
							suggestedList.push(record)
					})
				}

				suggestedList = vm.addDistanceToJobs(suggestedList)

				vm.suggestedJobs = vm.sortAndTrimSuggestedJobs(suggestedList, 10)

				vm.suggestedJobsLoading = false
			}).catch(() => {
				vm.suggestedJobsLoading = false
			})
		},
		fetchJobsOrServices(type) {
			let jobFields = [
				"id",
				"slug",
				"job_name",
				"job_num",
				"lat",
				"long",
				"suite",
				"city",
				"address",
				"postal_code",
				"check_in_tolerance",
				"description",
				"customer_id",
				"completed",
				"PayBoost",
				"Customer",
				"detailed_timelogs",
				"detailed_timelogs_depth",
				"JobBid"
			]

			return new Promise((resolve,reject) => {

				if (type == 'projects') {
					this.fetchJobs(["PayBoost","JobBid"],[["date_completed",null],["archived",0]],jobFields).then(response => {
						resolve(response)
					})
				}
				else {
					this.fetchServices(["PayBoost"],[
						['completed',null],
						['archived',0]
					],
					jobFields).then(response => {
						resolve(response)
					})
				}

			})
		},
		fetchSuggestedEquipment() {
			let vm = this

			this.suggestedEquipment = []

			return new Promise((resolve,reject) => {

				let params = {
					filters: [
						["user_id", this.$store.userDetails.id]
					],
					limit: 1,
					orders: [["time_in","DESC"]]
				}
				this.getJSON("TimeLog/get",params).then(response => {
						
					let params = {
						filters: [
							['assigned_user_id', this.$store.userDetails.id],
							["archived",0]
						]
					}

					if (response.success && response.data.length > 0) {
						params.filters.push(["id",response.data[0].equipment_id,"or"])
						params.filters.push(["archived",0])
					}

					this.getJSON("Equipment/get",params).then(response => {
						if (response.success)			
							vm.suggestedEquipment = response.data
						
						resolve(response)
					})
					
				})
			})
		},
		fetchData() {
			let vm = this

			let timeLogParams = {
				orders: [["time_out","ASC"]],
				filters: [
					["time_out","0000-00-00 00:00:00"],
					["user_id",this.$store.userDetails.id]
				],
				include: [
					"PayBoost"
				]
			}

			let scheduleParams = {
				filters: [
					["user_id",this.$store.userDetails.id],
					["end_time >",this.moment().format("YYYY-MM-DD HH:mm")] //end time greater than now
				],
				orders: [
					["start_time","ASC"]
				],
				include: [
					{model: "Job",fields: ["job_name","job_num","lat","long"]},
					{model: "Service",fields: ["job_name","job_num","lat","long"]}
				]
			}



			let promises = [	
				this.getJSON("TimeLog/get",timeLogParams),
				this.getJSON("TimeLogSetting/get"),
				//this.getLocation(),
				this.fetchUsers([],false,["first_name","last_name","email"]),
				this.getJSON("ScheduleEvent/get",scheduleParams)
			]

			this.spinner(true)

			Promise.allSettled(promises).then(responses => {
				vm.spinner(false)
				vm.showUI = true

				if (responses[3].value.success) {
					vm.userScheduleEvents = responses[3].value.data

					if (vm.scheduledEvent) {
						let type
						if (vm.scheduledEvent.service_id > 0)
							type = "services"
						else
							type = "projects"

						vm.fetchJobsOrServices(type)
					}
				}

				//////////////////////
				// TIME LOG SETTINGS
				//////////////////////

				if (responses[1].value.success) 
					vm.$store.timeLogSettings = responses[1].value.data[0]

				////////////////
				//	TIME LOGS
				////////////////
				if (responses[0].value.success)
					vm.userTimeLogs = responses[0].value.data

				//figure out if user is clocking in or out
				if (!("userTimeLogs" in vm) || "userTimeLogs" in vm && vm.userTimeLogs.length == 0) {
					vm.clockUIState = "out"
					vm.loadSuggestedJobs()

					if ("jobType" in vm.$route.params) {
						//we have a preselected job/project
						if (vm.$route.params.jobType == 'project') {
							vm.spinner(true)

							vm.fetchJobsOrServices("projects").then(response => {
								let project = vm.$store.projects.find(job => job.slug == vm.$route.params.jobSlug)
								vm.spinner(false)
								if (project) {
									vm.setClockInJobType("Project")
									vm.setProject(project.id,"project")
									
									vm.log.job_id = project.id
								}
							})
						}
						else if (vm.$route.params.jobType == 'service') {
							vm.spinner(true)
							vm.fetchJobsOrServices("services").then(response => {
								vm.spinner(false)
								let service = vm.$store.services.find(job => job.slug == vm.$route.params.jobSlug)

								if (service) {
									vm.setClockInJobType("Service")
									vm.setProject(service.id,"service")
									
									vm.log.service_id = service.id
								}
							})
						}
					}
				}
				else {

					//check logs to see if there is one open
					vm.userTimeLogs.forEach((log,index) => {
						if (log.time_out == "0000-00-00 00:00:00") {
							vm.clockingIn = false

							vm.log = log

							if (vm.log.regular_hours == 0)
								vm.log.regular_hours = ""

							if (vm.log.overtime_hours_15x == 0)
								vm.log.overtime_hours_15x = ""

							if (vm.log.overtime_hours_2x == 0)
								vm.log.overtime_hours_2x = ""

							if (vm.log.overtime_hours_25x == 0)
								vm.log.overtime_hours_25x = ""

							//calculate elapsed time from request timestamp
							let time_now = new Date(responses[1].value.time)
							let log_time = new Date(log.time_in);

							let dif_seconds = (time_now.getTime() - log_time.getTime())/1000;
							let dif_hrs = Math.round(dif_seconds/60/6)/10;

							vm.log.elapsed = false
							if (!isNaN(dif_hrs))
								vm.log.elapsed = dif_hrs;

							//set project
							if (log.job_id > 0) {

								vm.fetchJobsOrServices("projects").then(response => {
									let theProject = vm.$store.projects.find(job => job.id == log.job_id)

									if (theProject) {
										vm.project = theProject
										vm.projectType = "Project"

										vm.project.PayBoost.forEach(item => {
											if (!("payBoosts" in vm.log))
												log.payBoosts = {}

											vm.log.payBoosts[item.id] = 0;
										})

										vm.projectLocation = {
											lat: +theProject.lat,
											lng: +theProject.long
										}
									}
								})
							}
							else {

								vm.fetchJobsOrServices("services").then(response => {
									let theService = vm.$store.services.find(service => service.id == log.service_id)

									if (theService) {
										vm.project = theService
										vm.projectType = "Service"

										vm.project.PayBoost.forEach(item => {
											if (!("payBoosts" in vm.log))
												log.payBoosts = {}

											vm.log.payBoosts[item.id] = 0;
										})

										vm.projectLocation = {
											lat: +theService.lat,
											lng: +theService.long
										}
									}
								})
							}

							if (log.clocked_in_offsite == 1 && log.time_on_site == "0000-00-00 00:00:00") 
								vm.clockUIState = "offsite"
							else
								vm.clockUIState = "in"

						}
						else {
							vm.log = {
								job_id: null,
								service_id: null,
								lat: null,
								long: null,
								position_accuracy: null,
								reason_not_on_site: null,
								payBoosts: [], //this will get discarded by API
								PayBoost: [] //primitized -- this is what the API will care about
							}
						}
					})
				}
			})
		}
	},
	created() {
		this.fetchData()

		if (this.$store.latestPosition)
			this.currentPosition = this.$store.latestPosition

		/*this.positionRefreshTimer = setInterval(() => {
			
			vm.getLocation().then(pos => {
				
				vm.currentPosition = pos
			})
		},5000)*/

	}

}
</script>

<style type="text/css" scoped>
	.container {
		max-width: 800px;
	}

	.inline-label {
		display: inline-block;
		width: 200px;
	}


	.suggested-job, .suggested-equipment {
		width: 100%;
		padding: 5px;
		border: 1px solid #e0e0e0;
		margin-bottom: 5px;
	}

	.suggested-jobs .job-map-container {
		width: 400px;
	}

	.suggested-jobs {
		margin-bottom: 16px;
	}

	.suggested-jobs h3 {
		margin-bottom: 12px;
		font-weight: 600;
		color: #334155;
	}

	.suggested-jobs-list {
		margin-left: -19px;
		margin-right: -19px;
	}

	.clockin-sections {
		display: flex;
		flex-direction: column;
		gap: 14px;
		padding-bottom: 8px;
	}

	.clockin-section {
		padding: 16px 18px;
		background-color: #fff;
		border: 1px solid rgba(15, 23, 42, 0.08);
		border-radius: 14px;
		box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 6px 16px rgba(15, 23, 42, 0.04);
	}

	.clockin-section.disabled {
		background-color: #f8fafc;
		border-color: rgba(15, 23, 42, 0.05);
		box-shadow: none;
		opacity: 0.72;
	}

	.clockin-section h3 {
		font-size: 14pt;
		font-weight: 600;
		color: #1e293b;
		cursor: pointer;
		margin-bottom: 10px;
	}

	.clockin-section-value {
		text-align: center;
		font-size: 18pt;
		color: green;
		cursor: pointer;
	}

	.clockin-section-value:hover {
		opacity: 0.7;
	}

</style>